import { useEffect, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import useStyles from "./styles";
import api from "../../../services/api";
import corNeutraChip from "../corNeutraChip";
import alvoId from "../alvoId";
import rotuloAlvo from "../rotuloAlvo";

// Classifica cada cliente do resultado da rotina em massa - a unidade de acao de scripts e o
// cliente inteiro (uma execucao so por cliente), entao o resultado por cliente e um unico status,
// nao uma lista de chaves atualizadas/puladas/falhadas como em templates
function statusCliente(cliente) {
	if (!cliente?.coletaOk) return "falha_coleta";
	if (cliente?.erroInterno) return "erro_interno";
	if (!cliente?.temPendencia) return "sem_pendencia";
	// Lacuna de cadastro, nao falha de operacao - nunca chegou a tentar SSH
	if (cliente?.temAcessoVm === false) return "sem_acesso_vm";
	return cliente?.resultado?.status || "falha";
}

const ROTULO_SITUACAO = {
	falha_coleta: "Falha na coleta",
	erro_interno: "Erro interno",
	sem_pendencia: "Nada a atualizar",
	sem_acesso_vm: "Sem acesso VM cadastrado",
	sucesso: "Atualizado",
	abortado_divergencia: "Pulado por divergência",
	falha: "Falha",
};

export default function ModalAtualizarTodosScripts({ open, onClose, scripts, onConcluido }) {
	const [confirmado, setConfirmado] = useState(false);
	const [enviando, setEnviando] = useState(false);
	const [resultado, setResultado] = useState(null);
	const [erroEnvio, setErroEnvio] = useState(null);
	const theme = useTheme();
	const styles = useStyles(theme);

	// Reseta o estado toda vez que o modal reabre - o resultado de uma rodada anterior nao pode
	// vazar pra proxima abertura
	useEffect(() => {
		if (!open) return;
		setConfirmado(false);
		setEnviando(false);
		setResultado(null);
		setErroEnvio(null);
	}, [open]);

	// POST /versionamento/scripts/rotina sem clienteId - roda a rotina completa pra todos os
	// clientes de uma vez. Erro de rede/500 aqui e generico, nao se confunde com falha individual
	// de cliente (que ja vem dentro de resumoGeral/clientes)
	async function handleConfirmarAtualizacao() {
		setEnviando(true);
		setErroEnvio(null);
		try {
			const response = await api.post("/versionamento/scripts/rotina", {});
			setResultado(response?.data || null);
		} catch (error) {
			setErroEnvio(
				error?.response?.data?.error ||
					"Não foi possível enviar a atualização. Tente novamente.",
			);
		} finally {
			setEnviando(false);
		}
	}

	// Fecha o modal - se ja havia um resultado (rotina enviada), dispara o refresh do grid do pai antes
	function handleFechar() {
		if (enviando) return;
		if (resultado) {
			onConcluido?.();
		}
		onClose?.();
	}

	// Par fundo/texto por situacao de cliente - mesmos tokens de chip.background/colorText ja
	// usados no resto da feature, sem token novo
	function corSituacao(situacao) {
		if (situacao === "falha" || situacao === "falha_coleta" || situacao === "erro_interno") {
			return {
				background: theme.palette.chip.background.critical,
				color: theme.palette.chip.colorText.critical,
			};
		}
		if (situacao === "abortado_divergencia" || situacao === "sem_acesso_vm") {
			return {
				background: theme.palette.chip.background.high,
				color: theme.palette.chip.colorText.high,
			};
		}
		if (situacao === "sucesso") {
			return {
				background: theme.palette.chip.background.success,
				color: theme.palette.chip.colorText.success,
			};
		}
		return corNeutraChip(theme);
	}

	// Motivo/tooltip por situacao - cada campo vive num lugar diferente do objeto retornado pelo backend
	function motivoSituacao(cliente, situacao) {
		if (situacao === "falha_coleta") return cliente?.motivoFalhaColeta;
		if (situacao === "erro_interno") return cliente?.erroInterno;
		if (situacao === "sem_acesso_vm") {
			return cliente?.proxy
				? "Nenhum acesso VM com a tag Proxy cadastrado para este cliente."
				: "Acesso VM não cadastrado para este cliente.";
		}
		if (situacao === "abortado_divergencia" || situacao === "falha") return cliente?.resultado?.motivo;
		return null;
	}

	const listaScripts = scripts || [];
	// Todos os scripts com pelo menos 1 cliente pendente (desatualizado ou sem o script), independente
	// de bloqueio por divergencia - o bloqueio so e decidido depois, na chamada de verdade. Conta por alvo
	// (cliente + proxy): cada proxy regional e uma execucao propria
	const scriptsAfetados = listaScripts.filter((script) => script?.desatualizado > 0 || script?.ausente > 0);
	const clientesAfetados = new Set();
	scriptsAfetados.forEach((script) => {
		script?.clientes?.forEach((cliente) => {
			if (cliente?.status === "Desatualizado" || cliente?.status === "Ausente no cliente") {
				clientesAfetados.add(alvoId(cliente));
			}
		});
	});

	const resumoGeral = resultado?.resumoGeral;
	const clientesResultado = resultado?.clientes || [];

	return (
		<Dialog open={open} onClose={handleFechar} fullWidth maxWidth="sm">
			<DialogTitle>Atualizar todos os scripts pendentes</DialogTitle>
			<DialogContent>
				{!resultado && (
					<Box>
						<Typography variant="body2" sx={styles.resumo}>
							<strong>{scriptsAfetados.length} scripts pendentes</strong> afetando{" "}
							<strong>{clientesAfetados.size} alvos (cliente · proxy)</strong>
						</Typography>
						<Box sx={styles.listaScripts}>
							{scriptsAfetados.map((script) => (
								<Box key={script?.chave} sx={styles.linhaScript}>
									<Typography variant="body2" sx={styles.nomeScript}>
										{script?.nome || script?.chave}
									</Typography>
									<Typography variant="caption" sx={styles.contagemScript}>
										{script?.desatualizado || 0} desatualizado(s), {script?.ausente || 0} sem o script
									</Typography>
								</Box>
							))}
						</Box>
						<Typography variant="body2" sx={styles.aviso}>
							A atualização roda uma vez por alvo (cliente · proxy): baixa todos os scripts desatualizados
							daquele alvo e instala os que faltam, de uma vez. Alvos com divergência — coleta nunca feita, com
							falha ou parada há mais de 24h — serão pulados automaticamente e registrados no
							histórico.
						</Typography>
						<Typography variant="body2" sx={styles.aviso}>
							Cada alvo roda em sequência e pode levar cerca de 30 a 60 segundos — com
							muitos alvos pendentes, a operação pode demorar vários minutos. Não
							feche esta janela antes do resultado aparecer.
						</Typography>
						<Box sx={styles.confirmacaoContainer}>
							<FormControlLabel
								control={
									<Checkbox
										checked={confirmado}
										disabled={enviando}
										onChange={(event) => setConfirmado(event.target.checked)}
									/>
								}
								label="Confirmo que quero atualizar e instalar os scripts pendentes de todos os clientes listados"
							/>
						</Box>
						{erroEnvio && (
							<Typography variant="body2" sx={styles.erro}>
								{erroEnvio}
							</Typography>
						)}
					</Box>
				)}
				{resultado && (
					<Box sx={styles.resultadoContainer}>
						<Box sx={styles.resumoGeralContainer}>
							<Chip
								size="small"
								label={`${resumoGeral?.clientesAtualizados || 0} atualizados`}
								sx={{
									...styles.chip,
									backgroundColor: theme.palette.chip.background.success,
									color: theme.palette.chip.colorText.success,
								}}
							/>
							<Chip
								size="small"
								label={`${resumoGeral?.clientesPulados || 0} pulados por divergência`}
								sx={{
									...styles.chip,
									backgroundColor: theme.palette.chip.background.high,
									color: theme.palette.chip.colorText.high,
								}}
							/>
							<Chip
								size="small"
								label={`${resumoGeral?.clientesFalhados || 0} falhados`}
								sx={{
									...styles.chip,
									backgroundColor: theme.palette.chip.background.critical,
									color: theme.palette.chip.colorText.critical,
								}}
							/>
						</Box>
						{clientesResultado.length === 0 && (
							<Typography variant="body2" sx={styles.semItens}>
								Nenhum cliente foi processado.
							</Typography>
						)}
						{clientesResultado.map((cliente) => {
							const situacao = statusCliente(cliente);
							const cor = corSituacao(situacao);
							const motivo = motivoSituacao(cliente, situacao);
							const aviso = situacao === "sucesso" ? cliente?.resultado?.recoletaAviso : null;
							return (
								<Box key={alvoId(cliente)} sx={styles.linhaResultado}>
									<Typography variant="body2" sx={styles.nomeResultado}>
										{rotuloAlvo(cliente) || cliente?.clienteId}
									</Typography>
									<Tooltip title={motivo || ""}>
										<Chip
											size="small"
											label={ROTULO_SITUACAO[situacao] || situacao}
											sx={{
												...styles.chip,
												backgroundColor: cor.background,
												color: cor.color,
											}}
										/>
									</Tooltip>
									{aviso && (
										<Typography variant="caption" sx={styles.avisoResultado}>
											{aviso}
										</Typography>
									)}
								</Box>
							);
						})}
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				{!resultado ? (
					<>
						<Button onClick={handleFechar} disabled={enviando}>
							Cancelar
						</Button>
						<Button
							variant="contained"
							disabled={enviando || !confirmado}
							onClick={handleConfirmarAtualizacao}
						>
							{enviando ? <CircularProgress size={20} /> : "Confirmar atualização"}
						</Button>
					</>
				) : (
					<Button variant="contained" onClick={handleFechar}>
						Fechar
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
}
